import { Modal } from "antd";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useBeforeUnload, useBlocker } from "react-router-dom";

type DraftRegistration = { isDirty: () => boolean; discard: () => void };
const DraftsContext = createContext<Map<string, DraftRegistration> | null>(
  null,
);

/** One route blocker protects every open form, including nested drawers. */
export function UnsavedDocumentsProvider({
  children,
}: {
  children: ReactNode;
}) {
  const registrations = useRef(new Map<string, DraftRegistration>());
  const isDirty = useCallback(
    () => [...registrations.current.values()].some((d) => d.isDirty()),
    [],
  );
  const blocker = useBlocker(isDirty);
  useBeforeUnload(
    useCallback(
      (event) => {
        if (isDirty()) {
          event.preventDefault();
          event.returnValue = "";
        }
      },
      [isDirty],
    ),
  );
  return (
    <DraftsContext.Provider value={registrations.current}>
      {children}
      <Modal
        zIndex={2000}
        open={blocker.state === "blocked"}
        title="单据尚未保存"
        okText="放弃并离开"
        cancelText="留在当前页"
        onCancel={() => {
          if (blocker.state === "blocked") blocker.reset();
        }}
        onOk={() => {
          for (const draft of registrations.current.values()) draft.discard();
          if (blocker.state === "blocked") blocker.proceed();
        }}
      >
        <p>离开后，本次未保存内容将丢失。可以继续填写并保存。</p>
      </Modal>
    </DraftsContext.Provider>
  );
}

export function useUnsavedDocument() {
  const registrations = useContext(DraftsContext);
  const id = useId();
  const dirtyRef = useRef(false);
  const [pendingClose, setPendingClose] = useState<(() => void) | null>(null);
  const markDirty = useCallback(() => {
    dirtyRef.current = true;
  }, []);
  const markSaved = useCallback(() => {
    dirtyRef.current = false;
    setPendingClose(null);
  }, []);
  const isDirty = useCallback(() => dirtyRef.current, []);
  useEffect(() => {
    if (!registrations) throw new Error("UnsavedDocumentsProvider is required");
    registrations.set(id, { isDirty, discard: markSaved });
    return () => {
      registrations.delete(id);
    };
  }, [registrations, id, isDirty, markSaved]);
  const requestClose = useCallback((close: () => void) => {
    if (!dirtyRef.current) close();
    else setPendingClose(() => close);
  }, []);
  const dialog = (
    <Modal
      zIndex={2000}
      open={pendingClose !== null}
      title="放弃当前单据？"
      okText="放弃填写"
      cancelText="继续填写"
      onCancel={() => setPendingClose(null)}
      onOk={() => {
        const close = pendingClose;
        markSaved();
        close?.();
      }}
    >
      <p>确认放弃本次填写的内容？</p>
    </Modal>
  );
  return { markDirty, markSaved, isDirty, requestClose, dialog };
}
